import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { ColumnLayoutService } from '@frontend/sports/layout/feature';
import { isEqual, round } from 'lodash-es';
import { distinctUntilChanged, filter, map, merge, skip } from 'rxjs';

import { Slide } from './match-simulation.models';
import { MatchSimulationService } from './match-simulation.service';

interface Size {
    width: number;
    height: number;
}

@Component({
    selector: 'ms-match-simulation',
    templateUrl: './match-simulation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TrustPipe],
})
export class MatchSimulationComponent implements OnInit, OnChanges {
    @Input() homeTeam: string;
    @Input() awayTeam: string;
    @Input() runningBall: string;
    @Input() inColumn = false;
    @Input() size?: Size;
    @Input() isColumnReSize = true;

    @HostBinding('class') className = 'match-simulation';

    url: Nullable<string> = null;
    ratio = 438 / 600;
    base = { width: 320, height: 205 };
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private simulationService: MatchSimulationService,
        private dispatcher: DispatcherService,
        private timerService: TimerService,
        private columnService: ColumnLayoutService,
        private changeDetector: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        if (!this.isColumnReSize) {
            return;
        }
        merge(
            this.columnService.rightColumnSizeChanged,
            this.dispatcher.on<Slide>('SLIDE_SET').pipe(
                filter((slide) => slide.params.slideName === 'match-simulation'),
                skip(1),
            ),
        )
            .pipe(
                map(() => this.getSize()),
                distinctUntilChanged(isEqual),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((size) => {
                this.size = size;
                this.updateUrl(this.getUrl(size.width, size.height));
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.size) {
            this.size = this.getSize();
        }
        if (changes.runningBall) {
            this.updateUrl(this.getUrl(this.size.width, this.size.height));
        }
    }

    private getUrl(width: number, height: number): string {
        return this.simulationService.getMatchSimulationUrl({
            width,
            height,
            runningBallDataId: this.runningBall,
            homeTeam: this.homeTeam,
            awayTeam: this.awayTeam,
        });
    }

    private getSize(): Size {
        const mobileModeWidth = Math.min(this.columnService.currentCenterColumnSize.width, 430);
        const width = this.inColumn ? this.columnService.currentRightColumnSize.width : mobileModeWidth;

        if (width) {
            return { width: round(width), height: round(width * this.ratio) };
        } else {
            return this.base;
        }
    }

    private updateUrl(url: string): void {
        // DeveloperLog - Chapter 1 - Entry 1.
        // We have to use ng-if here because otherwise setting the src of the iFrame multiple times, adds it to the browser history :/
        this.url = null;
        this.timerService.setTimeout(() => {
            this.url = url;
            this.changeDetector.markForCheck();
        });
    }
}
